'use client';

import React from 'react';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import Container from '@/ui/components/shared/Container';
import { resolveVideoSource } from '@/utils/video-source';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { VideoText5050Media } from './video-text-5050-media';
import { VideoText5050Tile } from './video-text-5050-types';

export interface VideoText5050Props {
  tile: VideoText5050Tile;
  trackingPrefix?: string;
}

export function VideoText5050({ tile, trackingPrefix }: Readonly<VideoText5050Props>) {
  const source = resolveVideoSource(tile.video, tile['video-url']?.text);

  // The video carries the tile — without a playable source there is nothing worth rendering.
  if (!source) {
    return null;
  }

  return (
    <Container>
      <div data-testid="video-text-5050-grid" className="md:grid md:grid-cols-2 md:gap-14">
        <VideoText5050Media
          source={source}
          poster={tile.poster}
          videoSeo={tile['video-seo']}
          titleText={tile.title?.plainText?.[0]}
          aspectRatio={tile['aspect-ratio']?.options?.[0]?.value}
          maxWidth={tile['max-width']?.number}
          maxHeight={tile['max-height']?.number}
          isVideoRight={Boolean(tile['video-right']?.value)}
          trackingPrefix={trackingPrefix}
        />
        <div className="col-span-1 my-auto md:p-10 lg:p-12">
          <RichTextRenderer blocks={tile.title} className="text-2xl sm:text-3xl lg:text-4xl" />
          <RichTextRenderer
            blocks={tile.description}
            className="mt-2 sm:text-md md:mt-4 md:text-lg lg:text-xl"
          />
          <ButtonLink
            className="mt-3 w-fit sm:mt-6"
            ctaComponent={tile.cta}
            trackingPrefix={trackingPrefix}
          />
        </div>
      </div>
    </Container>
  );
}
